import React, { useMemo, memo } from 'react';
import { EnergyLogRecord } from '../../../hooks/useHistoricalData';

interface SelfConsumptionGaugeProps {
  data: EnergyLogRecord[];
  loading?: boolean;
}

export const SelfConsumptionGauge = memo(function SelfConsumptionGauge({ data, loading }: SelfConsumptionGaugeProps) {
  // Calculate self-consumption percentage from recent data
  const { selfConsumptionPercent, totalSolarKWh, selfConsumedKWh, exportedKWh } = useMemo(() => {
    if (data.length < 2) {
      return { selfConsumptionPercent: 0, totalSolarKWh: 0, selfConsumedKWh: 0, exportedKWh: 0 };
    }

    let totalSolar = 0;
    let selfConsumed = 0;
    let exported = 0;

    // Integrate power readings to get energy
    for (let i = 1; i < data.length; i++) {
      const prev = data[i - 1];
      const curr = data[i];
      const hoursElapsed = (curr.timestamp - prev.timestamp) / (1000 * 60 * 60);

      // Average power over interval, convert to kWh
      totalSolar += ((prev.solarPower + curr.solarPower) / 2) * hoursElapsed / 1000;
      selfConsumed += ((prev.solarToLoad + curr.solarToLoad) / 2) * hoursElapsed / 1000;
      exported += ((prev.solarToGrid + curr.solarToGrid) / 2) * hoursElapsed / 1000;
    }

    const percent = totalSolar > 0 ? (selfConsumed / totalSolar) * 100 : 0;

    return {
      selfConsumptionPercent: Math.round(percent),
      totalSolarKWh: Math.round(totalSolar * 10) / 10,
      selfConsumedKWh: Math.round(selfConsumed * 10) / 10,
      exportedKWh: Math.round(exported * 10) / 10,
    };
  }, [data]);

  // SVG donut parameters
  const size = 130;
  const strokeWidth = 20;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const selfConsumedOffset = circumference - (selfConsumptionPercent / 100) * circumference;
  const exportedOffset = circumference - ((100 - selfConsumptionPercent) / 100) * circumference;

  if (loading) {
    return (
      <div className="bg-card-bg border border-slate-700 rounded-lg p-4 h-64 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-solar border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
          <p className="text-slate-400 text-sm">Loading...</p>
        </div>
      </div>
    );
  }

  if (data.length === 0 || totalSolarKWh === 0) {
    return (
      <div className="bg-card-bg border border-slate-700 rounded-lg p-4 h-64 flex items-center justify-center">
        <div className="text-center">
          <svg className="w-10 h-10 text-slate-600 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
          </svg>
          <p className="text-slate-400 text-sm">No solar data</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card-bg border border-slate-700 rounded-lg p-4">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-semibold text-slate-300 uppercase tracking-wide">
          Self-Consumption
        </h3>
      </div>

      <div className="relative h-40 flex items-center justify-center">
        {/* CSS-based SVG donut with transitions */}
        <svg width={size} height={size} className="transform -rotate-90">
          {/* Background track */}
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke="#334155"
            strokeWidth={strokeWidth}
          />
          {/* Exported (blue) - bottom layer */}
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke="#3b82f6"
            strokeWidth={strokeWidth}
            strokeDasharray={circumference}
            strokeDashoffset={0}
            strokeLinecap="round"
            className="transition-all duration-700 ease-out"
          />
          {/* Self-consumed (green) - top layer, covers from start */}
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke="#22c55e"
            strokeWidth={strokeWidth}
            strokeDasharray={circumference}
            strokeDashoffset={selfConsumedOffset}
            strokeLinecap="round"
            className="transition-all duration-700 ease-out"
          />
        </svg>

        {/* Center text - transitions on number changes */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center">
            <div className="text-2xl font-bold text-white transition-all duration-300">
              {selfConsumptionPercent}%
            </div>
            <div className="text-xs text-slate-400">Self-Use</div>
          </div>
        </div>
      </div>

      {/* Legend - values transition smoothly */}
      <div className="flex justify-center gap-4 mt-2">
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-full bg-green-500"></div>
          <span className="text-xs text-slate-400 transition-all duration-300">
            Used: {selfConsumedKWh} kWh
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-full bg-blue-500"></div>
          <span className="text-xs text-slate-400 transition-all duration-300">
            Exported: {exportedKWh} kWh
          </span>
        </div>
      </div>

      {/* Total */}
      <div className="text-center mt-2 pt-2 border-t border-slate-700">
        <span className="text-xs text-slate-500">
          Total Solar: <span className="text-slate-300 font-medium transition-all duration-300">{totalSolarKWh} kWh</span>
        </span>
      </div>
    </div>
  );
});

export default SelfConsumptionGauge;
